/** @jsxImportSource @emotion/react */
import { ReactComponent as CheckIcon } from "../../img/header_icons/check.svg";
import { mainnet, useNetwork, useSwitchNetwork } from "wagmi";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { SUPPORTED_NETWORKS } from "../../types/network";
import "./NetworkDropdown.css";
import { NetworkButton } from "./NetworkButton";
import { getChainFromId } from "../../utils/chainUtils";
import { GlobalStore } from "../../store/global/GlobalStore";
import { updateUrlParams, URL_KEYS } from "../../utils/urlUtils";
import { NETWORK_NAME_TO_DISPLAY } from "../../config/consts";
import * as Sentry from "@sentry/react";
import { css } from "@emotion/react";
import { NetworkDropdownHeader } from "./elements/NetworkDropdownHeader";
import { NetworkDropdownContainer } from "./elements/NetworkDropdownContainer";
import { NetworkButtonProps, useNetworkButtonConfig } from "hooks/useNetworkButtonConfig";
import styled from "@emotion/styled";
import { withIsVisible } from "utils/reactUtils";
import { getTokens } from "../../store/global/getTokenMetadata";
import { cloneDeep } from "lodash-es";
import { Chain } from "viem/chains";
import { MODAL_SELECTORS } from "../Modal/instance/modalSelectors";

export interface NetworkDropdownProps {}

export const NetworkDropdown: React.FC<NetworkDropdownProps> = () => {
  const network = useNetwork();
  const theme = GlobalStore.useState(s => s.theme);
  const modalSelector = GlobalStore.useState(s => s.modalSelector);
  const storeNetwork = GlobalStore.useState(s => s.network);
  const networkId = network.chain?.id ?? storeNetwork.id;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isRequestingTokens, setIsRequestingTokens] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const { switchNetwork } = useSwitchNetwork({
    onSuccess: () => {
      GlobalStore.update(s => {
        s.tokens = undefined;
      });
      setIsDropdownOpen(false);
    },
    onError: error => {
      console.error(error);
      Sentry.captureException(`Could not switch networks`, { extra: { error } });
      updateUrlParams(URL_KEYS.NETWORK, storeNetwork.name.toLowerCase(), window.location.pathname);
    },
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const networkButtonConfig = useNetworkButtonConfig();

  const updateChain = useCallback(async (network: Chain | undefined) => {
    const chainMatch = SUPPORTED_NETWORKS.find(entry => entry.id === network?.id);
    if (network && chainMatch) {
      const tokens = await getTokens(chainMatch.name, false);
      GlobalStore.update(s => {
        s.tokens = tokens ?? {};
        s.network = cloneDeep(chainMatch) as typeof s.network;
      });
    }
    setIsRequestingTokens(false);
  }, []);

  useEffect(() => {
    // First test if the network is supported
    const isSupportedNetwork = !SUPPORTED_NETWORKS.map(network => network.id).includes(
      network.chain?.id ?? -1
    );
    if (network.chain && isSupportedNetwork) {
      GlobalStore.update(s => {
        s.modalSelector = MODAL_SELECTORS.SWITCH_NETWORK;
      });
      return;
    } else {
      if (modalSelector === MODAL_SELECTORS.SWITCH_NETWORK) {
        GlobalStore.update(s => {
          s.modalSelector = undefined;
        });
      }
    }
    if (network.chain?.id !== storeNetwork.id && !isRequestingTokens) {
      setIsRequestingTokens(true);
      void updateChain(network.chain);
    }
  }, [isRequestingTokens, modalSelector, network, storeNetwork.id, updateChain]);
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (containerRef?.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    },
    [containerRef]
  );

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [handleClickOutside]);

  const buttonConfig = networkButtonConfig[networkId];
  // eslint-disable-next-line
  const defaultButtonConfig = networkButtonConfig[mainnet.id] as NetworkButtonProps;
  const propsToSpread = buttonConfig !== undefined ? buttonConfig : defaultButtonConfig;
  const handleOnClick = useCallback(
    (networkId: number) => {
      const newChain = getChainFromId(networkId);
      // First get metadata for the new chain
      if (newChain) {
        if (switchNetwork) {
          // Remove network from url to prevent network switch back
          updateUrlParams(
            URL_KEYS.NETWORK,
            newChain?.name.toLowerCase() ?? "",
            window.location.pathname
          );
          switchNetwork(networkId);
        } else {
          // If switchNetwork is not defined - a wallet is not connect - just update state/
          if (newChain) {
            void updateChain(newChain);
            setIsDropdownOpen(false);
          }
        }
      }
    },
    [switchNetwork, updateChain]
  );

  return (
    <NetworkDropdownContainer theme={theme} data-testid={"network-dropdown"} ref={containerRef}>
      <NetworkButton
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        {...propsToSpread}
        isDropdownOpen={isDropdownOpen}
        ref={buttonRef}
      >
        <NetworkDropdownContent
          isVisible={isDropdownOpen}
          buttonSize={buttonRef.current?.clientWidth}
          className={`dropdown-content`}
          data-testid={"network-dropdown-dialog"}
        >
          <NetworkDropdownHeader theme={theme}>Select Network</NetworkDropdownHeader>
          {Object.entries(networkButtonConfig).map(([networkIdToSwitchTo, props]) => {
            const isSelected = networkId === props.id;
            const Icon = props.icon;

            return (
              <div
                key={networkIdToSwitchTo}
                data-testid={`${networkId === props.id ? "selected-" : ""}network-dropdown-option`}
              >
                <div
                  className="row g-0 pointer"
                  style={{
                    borderRadius: "14px",
                    padding: "13px 12px",
                  }}
                  css={css`
                    background: ${isSelected ? theme.surface.main.secondary.hover : "none"};

                    &:hover {
                      background-color: ${theme.surface.alt.secondary.hover};
                    }
                  `}
                  onClick={() => handleOnClick(Number(networkIdToSwitchTo))}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className={"network-container"}>
                      <span className="me-3">
                        <Icon width={22} height={22} color={props.backgroundColor} />
                      </span>
                      <span
                        className="me-2"
                        data-testid="network-label"
                        style={{
                          font: theme.fontFamilies.Primary,
                          fontWeight: theme.fontWeights["work-sans-2"],
                          fontSize: theme.fontSize["5"],
                        }}
                      >
                        {NETWORK_NAME_TO_DISPLAY[props.id]}
                      </span>
                    </div>
                    {isSelected && <CheckIcon stroke="white" />}
                  </div>
                </div>
              </div>
            );
          })}
        </NetworkDropdownContent>
      </NetworkButton>
    </NetworkDropdownContainer>
  );
};

// Sub-components

const NetworkDropdownContent = withIsVisible(
  styled.div<{ buttonSize: number | undefined }>(({ theme, buttonSize }) => {
    return {
      backgroundColor: theme.surface.alt.secondary.default,
      boxShadow: theme["Card Shadow"],
      borderRadius: "20px",
      position: "absolute",
      minWidth: "184px",
      padding: "10px",
      marginTop: "2px",
      color: "#ccd1d1",
      top: "58px",
      transform: buttonSize ? `translateX(-${buttonSize}px)` : "none",
      zIndex: "2",
      textAlign: "start",
    };
  })
);
